import { InlineTextField } from "@shared/components/fields/InlineTextField"
import { cx } from "@shared/components/untitledui/util/cx"
import type { CardStack } from "@shared/models/card-stack"
import type { Course, CourseDateAnnotationSingle } from "@shared/models/course"
import type { CourseDateCombined } from "@shared/models/course-date-combined"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { tracked } from "@supergrain/kernel/react"
import styles from "cc-frontend/components/planner/perspectives/month/lesson-title.module.css"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import { useEffect, useState } from "react"
import CourseDateAnnotation from "react-app/components/planner/card-stacks/CourseDateAnnotation"
import { guardedSave } from "react-app/lib/guarded-save"
import { useEmberService } from "react-app/providers/ember-service-context"
import { Button } from "react-aria-components"

interface Props {
  courseDate: CourseDateCombined
  lesson: CardStack
  course: Course
  planbook: Planbook
  rotationCalendar: RotationCalendar
  dateString: string
  /** `weekIndex.courseIndex` — the month grid's row-height bookkeeping key. */
  gridIndex: string
  recalcHeights?: (gridIndex: string) => void
  onLessonFocus?: () => void
}

/**
 * The Ember `Month::LessonTitle`, keeping its DOM contract (classes, module styles, data
 * attributes) — the month grid's height bookkeeping and the legacy drag hooks find cells by it.
 */
const LessonTitle = tracked(function LessonTitle({
  courseDate,
  lesson,
  course,
  planbook,
  rotationCalendar,
  dateString,
  gridIndex,
  recalcHeights,
  onLessonFocus,
}: Props) {
  const lessonEditor = useEmberService<LessonEditor>("lesson-editor")

  const [hasFocus, setHasFocus] = useState(false)

  const title = lesson.attributes.title ?? ""
  const canEdit = (lesson.meta.permissions ?? []).includes("can-edit-plan")

  // The Ember LessonTitle re-measured its grid row whenever the title changed hands.
  useEffect(() => {
    recalcHeights?.(gridIndex)
  }, [title])

  const models = {
    cardStack: lesson as CardStack & { attributes: { usedAs: "lesson" } },
    dateString,
    planbook,
    course,
    rotationCalendar,
  }

  const handleChange = guardedSave((value: string, previousValue: string) => {
    // SimpleContentEditable's notifyValueUpdate guard: an unchanged non-empty value is not an edit.
    if (value !== "" && value === previousValue) return
    lessonEditor.editTitle(models, lesson.id, value, false)
  })

  const handleCommit = guardedSave((value: string, previousValue: string) => {
    lessonEditor.finishEditingTitle(models, lesson.id, value, previousValue)
    recalcHeights?.(gridIndex)
  })

  return (
    <div
      className={cx(
        "take-course-background-color js-lesson-exterior set-course-color-vars",
        styles["lesson-title"],
        hasFocus && styles["--has-focus"],
      )}
      data-color={course.attributes.color}
      data-hide-from-calendar={courseDate.hideFromCalendar || undefined}
      data-grid-index={gridIndex}
      data-course-id={course.id}
      data-date-string={dateString}
      data-is-new-lesson={(courseDate.showLesson && courseDate.isNewLesson) || undefined}
      data-card-stack-id={lesson.id}
      data-is-non-instructional={course.attributes.isNonInstructional || undefined}
    >
      <div
        className={cx(
          "js-lesson-interior",
          styles["lesson-title__inner"],
          hasFocus && styles["--has-focus"],
        )}
        data-course-id={course.id}
        data-date-string={dateString}
        data-card-stack-id={lesson.id}
        data-lesson-title={lesson.attributes.title ?? undefined}
      >
        {courseDate.showLesson ? (
          <>
            <div
              className={cx(
                "js-lesson-move-pad",
                styles["move-pad"],
                canEdit && "js-can-drag-lesson",
              )}
            ></div>
            {course.attributes.isNonInstructional ? (
              <Button
                className={cx(styles["lesson-title__input"], "text-left")}
                onPress={() => onLessonFocus?.()}
                onFocus={() => {
                  setHasFocus(true)
                  onLessonFocus?.()
                }}
                onBlur={() => setHasFocus(false)}
              >
                {course.attributes.title}
              </Button>
            ) : (
              <InlineTextField
                aria-label="Lesson title"
                isReadOnly={!canEdit}
                value={title}
                placeholder="Title"
                className={styles["lesson-title__input"]}
                data-card-stack-id={lesson.id}
                onChange={handleChange}
                onCommit={handleCommit}
                onClick={() => onLessonFocus?.()}
                onFocus={() => {
                  setHasFocus(true)
                  onLessonFocus?.()
                }}
                onBlur={() => setHasFocus(false)}
              />
            )}
          </>
        ) : (
          courseDate.hasVisibleAnnotations &&
          courseDate.visibleAnnotations
            // The calc only ever puts single-date annotations here; this narrows the union.
            .filter(
              (annotation): annotation is CourseDateAnnotationSingle =>
                annotation.type === "date-annotation-course-custom-single",
            )
            .map((annotation) => (
              <CourseDateAnnotation
                annotation={annotation}
                course={course}
                location="onMonth"
                key={annotation.id}
              />
            ))
        )}
      </div>
    </div>
  )
})

export default LessonTitle
